import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AlertTriangle } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { TurnBudgetPause } from "../../../chat/hooks/useStreamingTurn";

export interface BudgetPausePromptProps {
  pause: TurnBudgetPause;
  onDecide: (decision: "continue" | "stop") => void | Promise<void>;
}

const DIMENSION_LABEL: Record<TurnBudgetPause["dimension"], string> = {
  cost: "cost limit",
  time: "time limit",
  iterations: "step limit",
};

/**
 * The turn paused because a budget dimension (cost/time/iterations) was
 * exceeded (budget_paused). Mirrors PlanApprovalCard/ConfirmationCard's
 * shape: a Soft card with a busy-guarded action row. Continue/Stop POST a
 * budget_decision to /steer (the parent supplies onDecide).
 */
export function BudgetPausePrompt({ pause, onDecide }: BudgetPausePromptProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reduce = useReducedMotion() ?? false;

  const run = (decision: "continue" | "stop") => async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide(decision);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
    >
      <Soft
        variant="raised"
        role="region"
        aria-label="Budget paused"
        sx={{
          my: 1.5,
          p: 1.5,
          boxShadow: (t) =>
            `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.warning.main, 0.45)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "warning.main" }}>
          <AlertTriangle size={16} aria-hidden />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Turn paused — {DIMENSION_LABEL[pause.dimension] ?? pause.dimension}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.primary", mb: 1.25 }}>
          {pause.reason}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="contained" disabled={busy} onClick={run("continue")}>
            Continue
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={busy} onClick={run("stop")}>
            Stop
          </Button>
        </Box>
        {/* useChatSession.sendMessage resolves the pause as Stop and starts a
            new turn; say so, so a retry typed here is not a surprise. */}
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
          Sending a new message stops this turn and starts a new one.
        </Typography>
        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Soft>
    </motion.div>
  );
}
